import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import {
  ArrowUpTrayIcon,
  DocumentPlusIcon,
  FolderPlusIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import {
  NEXTFLOW_CONFIG,
  SCRIPT_PIPE_FOOTER,
  SCRIPT_PIPE_HEADER,
} from "@via/components/tree-structure/helpers/constants.tsx";
import {
  createPath,
  updateChildPath,
} from "@via/components/tree-structure/helpers/utils.tsx";
import TreeNode from "@via/components/tree-structure/tree-node.tsx";
import { NOTIFICATION } from "@via/context/notifications-context.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import {
  AdvancedFilesActions,
  TEXT_FILES_EXTENSIONS,
  TREE_FILE_MESSAGES,
} from "@via/utils/constants.tsx";
import {
  type AnyRef,
  type TreeData,
  type TreeDataScriptInterface,
} from "@via/utils/interfaces.tsx";
import {
  forwardRef,
  type JSX,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  type CreateHandler,
  type DeleteHandler,
  type NodeApi,
  type RenameHandler,
  SimpleTree,
  Tree,
} from "react-arborist";
import Modal from "@via/components/modal.tsx";
import { useChangesDetect } from "@via/hooks/useChangesDetect";
import useCustomResizeObserver from "@via/hooks/useCustomResizeObserver.tsx";
import "@via/components/tree-structure/tree-structure-defaults.css";

const UNABLE_TO_DELETE = [
  NEXTFLOW_CONFIG,
  SCRIPT_PIPE_HEADER,
  SCRIPT_PIPE_FOOTER,
];
const MAX_UPLOAD_FILE_SIZE = 1 * 1024 * 1024; // 1MB

interface TreeStructureProps {
  ref?: AnyRef;
  treeData: TreeDataScriptInterface[];
  selection?: string | undefined;
  onSelect: (nodes: Array<NodeApi<TreeData>>) => void;
  openByDefault?: boolean;
  onTreeChange?: any;
  showMenu?: boolean;
  showSearch?: boolean;
  disableFolderSelection?: boolean;
}

let nextId = Math.floor(Math.random() * 1000);

function TreeStructureInner(props: TreeStructureProps, ref: any): JSX.Element {
  const { ref: parentRef, height } = useCustomResizeObserver();
  const inputFileRef = useRef<HTMLInputElement | null>(null);
  const [data, setData] = useState<TreeData[]>([]);
  const tree = useMemo(() => new SimpleTree<TreeData>(data), [data]);
  const [term, setTerm] = useState("");
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const {
    treeData,
    selection,
    onSelect,
    onTreeChange,
    showMenu = false,
    showSearch = false,
    openByDefault = false,
    disableFolderSelection = false,
  } = props;
  const { setNotification } = useNotifications();
  const { increaseChangeCount } = useChangesDetect();

  useEffect(() => {
    if (treeData) {
      setData(treeData);
    }
  }, [treeData]);

  const handleSelect = (nodes: Array<NodeApi<TreeData>>) => {
    // With disableMultiSelection, we expect at most one node.
    const selectedNode = nodes[0];
    if (disableFolderSelection && selectedNode && selectedNode.isInternal) {
      // Cancel selection for folders.
      selectedNode.deselect();
      return;
    }
    onSelect(nodes);
  };

  const onRename: RenameHandler<TreeDataScriptInterface> = ({
    id,
    name,
    node,
  }: {
    id: string;
    name: string;
    node: NodeApi;
  }) => {
    if (!onTreeChange || typeof onTreeChange !== "function") {
      return;
    }
    const existing = node.data.path;
    if (node.isLeaf) {
      node.data.path = createPath(node.parent, name);
      node.data.name = name;
    }
    if (node.isInternal) {
      const parent = createPath(node.parent, name);
      node.data = updateChildPath(node.data, parent);
      node.data.name = name;
      node.open();
    }
    onTreeChange(AdvancedFilesActions.Rename, node, existing);
    tree.update({ id, changes: { name } as any });
    setData(tree.data);
  };

  const onCreate: CreateHandler<TreeData> = ({ index, type }) => {
    increaseChangeCount();
    const data = { id: `${nextId++}`, name: "" } as any;
    if (type === "internal") {
      data.children = [];
    }
    if (type === "leaf") {
      data.path = "";
      data.text = "";
    }
    // Get the selected node
    // the reason, we'd like to create a file/folder under the selected node
    const selectedNode = ref.current?.selectedNodes?.[0];
    const parent = selectedNode?.isInternal
      ? selectedNode
      : selectedNode?.parent;
    const parentId =
      parent?.id === "__REACT_ARBORIST_INTERNAL_ROOT__"
        ? undefined
        : parent?.id;
    tree.create({ parentId, index, data });
    setData(tree.data);
    return data;
  };

  const onDelete: DeleteHandler<TreeData> = (args: {
    ids: string[];
    nodes: NodeApi[];
  }) => {
    const node = args.nodes[0];
    const next = node.next ?? node.prev;
    tree.drop({ id: node.id });
    setData(tree.data);
    next?.select();
  };

  const handleEditToggle = (node): void => {
    node?.edit();
  };

  const handleDelete = (node): void => {
    if (!onTreeChange || typeof onTreeChange !== "function") {
      return;
    }
    if (UNABLE_TO_DELETE.includes(node?.data?.name)) {
      setNotification({
        id: "tree-file-unable-to-delete",
        message: TREE_FILE_MESSAGES.UNABLE_TO_DELETE,
        details: TREE_FILE_MESSAGES.NEXTFLOW_REQUIRED,
        variant: NOTIFICATION.ERROR,
      });
      return;
    }
    onTreeChange(AdvancedFilesActions.Delete, node);
  };

  const handleFileUploadButtonClick = () => {
    increaseChangeCount();
    inputFileRef.current?.click();
  };

  const handleOnFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      return;
    }
    const filesArray = Array.from(files);
    setSelectedFiles(filesArray);
    setShowConfirmationModal(true);
    event.target.value = "";
  };

  const onCancelFileUpload = () => {
    setSelectedFiles([]);
    setShowConfirmationModal(false);
  };

  const onFileUpload = async () => {
    const allowedExtensions = TEXT_FILES_EXTENSIONS.split(", ");
    const newNodes: TreeData[] = [];

    const selectedNode = ref.current?.selectedNodes[0];
    const parent = selectedNode?.isInternal
      ? selectedNode
      : selectedNode?.parent;
    const parentId =
      parent?.id === "__REACT_ARBORIST_INTERNAL_ROOT__"
        ? undefined
        : parent?.id;

    for (const file of selectedFiles) {
      try {
        const fileParts = file.name.split(".");
        const fileExtension =
          fileParts.length > 1 ? `${fileParts.pop()!.toLowerCase()}` : "";
        // Lets allow fies without extension (eg. to support files like: Dockerfile)
        if (
          fileExtension !== "" &&
          !allowedExtensions.includes(`.${fileExtension.toLowerCase()}`)
        ) {
          setNotification({
            id: "tree-file-upload",
            message: "Invalid file type",
            details: "You can only upload text files with valid extensions",
            variant: NOTIFICATION.ERROR,
          });
          return;
        }
        if (file.size > MAX_UPLOAD_FILE_SIZE) {
          setNotification({
            id: "tree-file-upload",
            message: "File size too large",
            details: "You can only upload files up to 1MB",
            variant: NOTIFICATION.ERROR,
          });
          return;
        }
        const text = await file.text();
        const newId = `${nextId++}`;
        const path = createPath(parent, file.name);
        const newNode = {
          id: newId,
          name: file.name,
          path,
          text,
          type: "leaf",
        };
        newNodes.push(newNode);
      } catch (error) {
        console.log("error:", error);
      }
    }

    onTreeChange(AdvancedFilesActions.Upload, { data: newNodes });

    if (newNodes.length) {
      newNodes.forEach((node, i) => {
        tree.create({ parentId, index: i, data: node });
      });
      setData(tree.data);
    }
    setSelectedFiles([]);
    setShowConfirmationModal(false);
  };

  const createFileFolder = (
    <span className="isolate inline-flex rounded-md shadow-sm">
      <button
        type="button"
        title="New Folder"
        onClick={() => ref.current.createInternal()}
        className="folder-actions-button rounded-l-md px-3 py-2 text-gray-500">
        <FolderPlusIcon className="folder-actions-icon" />
      </button>
      <button
        type="button"
        title="New File"
        onClick={() => ref.current.createLeaf()}
        className="folder-actions-button -ml-px px-3 py-2 text-gray-500">
        <DocumentPlusIcon className="folder-actions-icon" />
      </button>
      <button
        type="button"
        title="Edit Folder / File"
        onClick={() => {
          handleEditToggle(ref.current.selectedNodes[0]);
        }}
        className="folder-actions-button -ml-px px-3 py-2 text-gray-500">
        <PencilSquareIcon className="folder-actions-icon" />
      </button>
      <input
        ref={inputFileRef}
        type="file"
        id="file-upload"
        className="hidden"
        accept={TEXT_FILES_EXTENSIONS}
        multiple
        onChange={handleOnFileChange}
      />
      <button
        type="button"
        title="Upload File"
        onClick={handleFileUploadButtonClick}
        disabled={ref?.current?.selectedNodes[0]?.isLeaf}
        className="folder-actions-button -ml-px px-3 py-2 text-gray-500">
        <ArrowUpTrayIcon className="mr-1 h-5 w-5" aria-hidden="true" />
      </button>
      <button
        type="button"
        title="Remove Folder / File"
        onClick={() => {
          handleDelete(ref.current.selectedNodes[0]);
        }}
        className="folder-actions-button -ml-px rounded-r-md text-gray-500">
        <TrashIcon className="folder-actions-icon" />
      </button>
    </span>
  );

  return (
    <>
      <div className="mb-1 mt-3">
        {showMenu && createFileFolder}
        {showSearch && (
          <div className="relative my-2 rounded-md shadow-sm">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <MagnifyingGlassIcon
                title="Search Files / Folders"
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </div>
            <input
              type="search"
              placeholder="Search..."
              id="search"
              value={term}
              onChange={e => {
                setTerm(e.target.value);
              }}
              className="block w-full rounded-md border-0 py-1.5 pl-10 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            />
          </div>
        )}
        {data.length > 0 && (
          <div role="button" onClick={e => e.preventDefault()}>
            <div className="sidebar">
              <div className="tree-parent" ref={parentRef}>
                <Tree
                  ref={ref}
                  data={data}
                  width={720}
                  height={height}
                  indent={12}
                  disableDrag={true}
                  disableDrop={true}
                  rowHeight={32}
                  openByDefault={openByDefault}
                  disableMultiSelection={true}
                  onCreate={onCreate}
                  onDelete={onDelete}
                  onRename={onRename}
                  onSelect={handleSelect}
                  selection={selection}
                  searchTerm={term}
                  disableEdit={
                    !onTreeChange || typeof onTreeChange !== "function"
                  }
                  searchMatch={(node: NodeApi, term) =>
                    node.data?.name.toLowerCase().includes(term.toLowerCase())
                  }>
                  {TreeNode}
                </Tree>
              </div>
            </div>
          </div>
        )}
        {showConfirmationModal && (
          <Modal
            open={showConfirmationModal}
            setOpen={setShowConfirmationModal}
            onConfirm={onFileUpload}
            onCancel={onCancelFileUpload}>
            <div className="flex flex-col py-2 text-sm leading-6 text-slate-600 md:py-3 lg:py-4">
              <p>
                You are about to upload files to the{" "}
                <span className="font-semibold">
                  {ref.current?.selectedNodes[0]?.data?.path || "/(root)"}
                </span>{" "}
                folder. Are you sure you want to proceed?
              </p>
            </div>
          </Modal>
        )}
      </div>
    </>
  );
}

export const TreeStructure = forwardRef(TreeStructureInner);
